// R-02c — the parts of the chat send that must hold even when Enter is pressed twice while an upload runs.
// Pure (no React) so they are tested by running them (utils/__tests__/r02cChatSendFlow.test.ts).

/** A synchronous re-entrancy lock (a React state flag is too late: two Enter presses can land before a re-render). */
export interface SendGate { busy: boolean }
export const createSendGate = (): SendGate => ({ busy: false });

/** Runs `fn` unless a send is already running; returns `{ ran: false }` for the second call. */
export async function runGuarded<T>(gate: SendGate, fn: () => Promise<T>): Promise<{ ran: true; value: T } | { ran: false }> {
  if (gate.busy) return { ran: false };
  gate.busy = true;
  try {
    return { ran: true, value: await fn() };
  } finally {
    gate.busy = false;
  }
}

/** Upload every file one by one; returns ONLY the ones that failed (they stay in the box for a retry). */
export async function uploadAll<F>(
  files: F[],
  upload: (file: F, index: number) => Promise<unknown>,
  onStart?: (index: number, total: number) => void
): Promise<F[]> {
  const failed: F[] = [];
  for (let i = 0; i < files.length; i++) {
    onStart?.(i, files.length);
    try { await upload(files[i], i); } catch { failed.push(files[i]); }
  }
  return failed;
}

export interface RetryState { commentId: string; ticketId: string }

/** A send with no new text, on the same ticket, after a partial failure attaches to the EXISTING message. */
export function retryTarget(retry: RetryState | null, ticketId: string, hasContent: boolean, hasFiles: boolean): string | null {
  return retry && retry.ticketId === ticketId && !hasContent && hasFiles ? retry.commentId : null;
}
